import styled, { css } from 'styled-components'
import { SCROLLBAR_GUTTER_SIZE } from './useOverlayScrollbar'

const SCROLLBAR_SIZE_PX = 6
export const SCROLLBAR_SIZE = `${SCROLLBAR_SIZE_PX}px`

const TRACK_BORDER_PX = 1
const TRACK_BORDER = `${TRACK_BORDER_PX}px`
const MIN_THUMB_SIZE = '24px'
// Cross-axis centering in the track. Along-axis inset is SCROLLBAR_THUMB_END_INSET.
const GUTTER_THUMB_INSET = `${
  (SCROLLBAR_GUTTER_SIZE - TRACK_BORDER_PX * 2 - SCROLLBAR_SIZE_PX) / 2
}px`

const paneRow = fillHeight => (fillHeight ? 'minmax(0, 1fr)' : 'minmax(0, auto)')
const gutterTrack = hasGutter => (hasGutter ? `${SCROLLBAR_GUTTER_SIZE}px` : null)

const hideNativeScrollbar = css`
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`

const thumbStyles = css`
  ${({ theme }) => css`
    position: absolute;
    z-index: 2;
    border-radius: 999px;
    background-color: ${theme.colors.gray3};
    opacity: 0;
    transition: opacity 0.15s ease, background-color 0.15s ease;
    cursor: pointer;

    &:hover {
      background-color: ${theme.colors.grayDark};
    }

    @media (hover: none) {
      display: none;
    }
  `}
`

const gutterThumbStyles = css`
  z-index: auto;
  opacity: 1;
  pointer-events: none;
`

const overlayThumb = {
  y: css`
    top: 0;
    right: ${TRACK_BORDER};
    width: ${SCROLLBAR_SIZE};
  `,
  x: css`
    left: 0;
    bottom: ${TRACK_BORDER};
    height: ${SCROLLBAR_SIZE};
  `,
}

const gutterThumb = {
  y: css`
    ${gutterThumbStyles}
    top: 0;
    left: ${GUTTER_THUMB_INSET};
    width: ${SCROLLBAR_SIZE};
  `,
  x: css`
    ${gutterThumbStyles}
    left: 0;
    top: ${GUTTER_THUMB_INSET};
    height: ${SCROLLBAR_SIZE};
  `,
}

export const VerticalThumb = styled.div`
  ${thumbStyles}
  ${({ $inGutter }) => ($inGutter ? gutterThumb.y : overlayThumb.y)}
  min-height: ${MIN_THUMB_SIZE};
`

export const HorizontalThumb = styled.div`
  ${thumbStyles}
  ${({ $inGutter }) => ($inGutter ? gutterThumb.x : overlayThumb.x)}
  min-width: ${MIN_THUMB_SIZE};
`

const gutterTrackStyles = css`
  position: relative;
  box-sizing: border-box;
  background-color: ${({ theme }) => theme.colors.gray2};
  cursor: pointer;

  &:hover ${VerticalThumb},
  &:hover ${HorizontalThumb} {
    background-color: ${({ theme }) => theme.colors.grayDark};
  }
`

export const Viewport = styled.div`
  ${({ $isActive, $reserveGutter, $fillHeight }) => css`
    position: relative;
    overflow: hidden;
    height: 100%;
    width: 100%;
    min-height: 0;
    min-width: 0;

    ${$reserveGutter &&
    css`
      /* Flex, not % height: nested flex + height:100% on GutterPack stays
         content-sized until a later resize (DevTools device mode), so the
         scroller never overflows and the wheel does nothing. */
      display: flex;
      flex-direction: column;
      align-items: flex-start;
    `}

    ${$reserveGutter &&
    !$fillHeight &&
    css`
      /* Beat GridScrollArea flex: 1 so the H-track sits under the table. */
      && {
        flex: 0 1 auto;
        height: auto;
        max-height: 100%;
      }
    `}

    ${!$reserveGutter &&
    $isActive &&
    css`
      ${VerticalThumb}, ${HorizontalThumb} {
        opacity: 1;
      }
    `}

    ${!$reserveGutter &&
    css`
      @media (hover: hover) {
        &:hover ${VerticalThumb}, &:hover ${HorizontalThumb} {
          opacity: 1;
        }
      }
    `}
  `}
`

export const GutterPack = styled.div`
  ${({ $fillHeight, $hasVerticalGutter, $hasHorizontalGutter }) => css`
    display: grid;
    align-items: stretch;
    justify-items: stretch;
    box-sizing: border-box;
    /* Width shrink-wraps to the table (track beside it) and clamps to the pane.
       Height comes from flex:1 / min-height:0 so the scroller is clipped on the
       first layout pass — height:100% is cyclic in this flex chain. */
    width: fit-content;
    max-width: 100%;
    min-width: 0;
    min-height: 0;
    ${$fillHeight
      ? css`
          flex: 1 1 0%;
        `
      : css`
          flex: 0 1 auto;
          height: auto;
          max-height: 100%;
        `}
    grid-template-columns: minmax(0, 1fr) ${gutterTrack($hasVerticalGutter)};
    grid-template-rows: ${paneRow($fillHeight)} ${gutterTrack($hasHorizontalGutter)};
  `}
`

export const ScrollerPane = styled.div`
  position: relative;
  overflow: hidden;
  min-width: 0;
  min-height: 0;
  width: 100%;
  height: 100%;

  ${({ $reserveGutter }) =>
    $reserveGutter &&
    css`
      grid-area: 1 / 1;
      width: 100%;
      height: 100%;
    `}

  ${({ $showRightBorder, theme }) =>
    $showRightBorder &&
    css`
      box-shadow: inset -${TRACK_BORDER} 0 0 ${theme.colors.gray3};
    `}
`

export const VerticalTrack = styled.div`
  ${gutterTrackStyles}
  grid-column: 2;
  grid-row: 1;
  min-width: ${SCROLLBAR_GUTTER_SIZE}px;
  border: ${TRACK_BORDER} solid ${({ theme }) => theme.colors.gray3};
  ${({ $joinCorner }) =>
    $joinCorner &&
    css`
      border-bottom: none;
    `}
`

export const HorizontalTrack = styled.div`
  ${gutterTrackStyles}
  grid-column: 1;
  grid-row: 2;
  min-height: ${SCROLLBAR_GUTTER_SIZE}px;
  border: ${TRACK_BORDER} solid ${({ theme }) => theme.colors.gray3};
  ${({ $joinCorner }) =>
    $joinCorner &&
    css`
      border-right: none;
    `}
`

export const GutterCorner = styled.div`
  box-sizing: border-box;
  grid-column: 2;
  grid-row: 2;
  background-color: ${({ theme }) => theme.colors.gray2};
  border: ${TRACK_BORDER} solid ${({ theme }) => theme.colors.gray3};
`

export const Scroller = styled.div`
  height: 100%;
  width: 100%;
  min-width: 0;
  min-height: 0;
  overflow: auto;
  ${hideNativeScrollbar}
`

export const ScrollerContent = styled.div`
  min-height: min-content;
  ${({ $reserveGutter }) =>
    $reserveGutter &&
    css`
      width: max-content;
    `}
`
